import { useState } from "react";
import { api } from "../lib/api";
import { mApi, getMemoriaToken, MEMORIA_TOKEN_KEY } from "../lib/memoria";
import { PinBoxes } from "./PinBoxes";
import { toast } from "sonner";
import { UserRound, X } from "lucide-react";

// Invito discreto e non invasivo: la lettura resta libera; la finestrella si apre
// solo se l'utente tocca "Dimmi chi è" dopo aver letto il responso.
export const FiguraInvite = ({ onOpen, onDismiss }) => (
  <div className="mt-5 flex items-center gap-3 rounded-xl border border-amber-500/25 bg-amber-500/5 px-4 py-3" data-testid="figura-invite">
    <UserRound className="w-4 h-4 text-amber-300 shrink-0" />
    <p className="flex-1 text-[11px] text-purple-100/70 leading-snug">
      Una figura è emersa chiaramente dalla stesa. Se vuoi, puoi dirmi chi è — questa interazione non consuma crediti.
    </p>
    <button onClick={onOpen} data-testid="figura-invite-open" className="gold-btn px-3 py-1.5 text-[10px] shrink-0">
      Dimmi chi è
    </button>
    <button onClick={onDismiss} data-testid="figura-invite-dismiss" className="p-1 text-purple-300/50 hover:text-purple-100 shrink-0 transition-colors" aria-label="Ignora">
      <X className="w-3.5 h-3.5" />
    </button>
  </div>
);

// Finestrella di riconoscimento figura: gratuita, facoltativa, salvata solo dietro PIN.
// La chiusura senza risposta NON è mai consenso alla memorizzazione stabile.
export const FiguraRiconosciuta = ({ figura, suggerita = null, readingId, onClose }) => {
  const [step, setStep] = useState("chiedi"); // chiedi | pin | consenso
  const [etichetta, setEtichetta] = useState("");
  const [noSuggerita, setNoSuggerita] = useState(false);
  const [person, setPerson] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pinReset, setPinReset] = useState(0);

  if (!figura) return null;
  const genereTxt = figura.genere === "maschile" ? " maschile" : figura.genere === "femminile" ? " femminile" : "";

  const salva = async (nome) => {
    const label = (nome ?? etichetta).trim();
    if (!label || busy) return;
    if (!getMemoriaToken()) { setEtichetta(label); setStep("pin"); return; }
    setBusy(true);
    try {
      const { data } = await mApi.post("/memoria/persone", { reading_id: readingId || "", figura, etichetta: label });
      setPerson(data);
      if (data.gia_interpellata) { toast.success("Anotazione salvata nella tua memoria."); onClose(); }
      else setStep("consenso");
    } catch {
      toast.error("Non sono riuscita a salvarla. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  const unlock = async (pin) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/memoria/unlock", { pin });
      sessionStorage.setItem(MEMORIA_TOKEN_KEY, data.token);
      await salva();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PIN non corretto. Riprova.");
      setPinReset((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  const consenso = async (stabile) => {
    if (person) {
      try { await mApi.post(`/memoria/persone/${person.id}/consenso`, { stabile }); } catch { /* non bloccante */ }
    }
    onClose();
  };

  const chiudiConsenso = async () => {
    if (person) {
      try { await mApi.post(`/memoria/persone/${person.id}/interpellata`, {}); } catch { /* non bloccante */ }
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="figura-popup" onClick={step === "consenso" ? chiudiConsenso : onClose}>
      <div className="mystic-card max-w-sm w-full p-6 sm:p-7 relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={step === "consenso" ? chiudiConsenso : onClose}
          data-testid="figura-close-btn"
          className="absolute top-3 right-3 p-1 text-purple-300/60 hover:text-purple-100 transition-colors"
          aria-label="Chiudi"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex justify-center mb-4">
          <span className="w-11 h-11 rounded-full border border-amber-500/40 bg-amber-500/10 flex items-center justify-center">
            <UserRound className="w-5 h-5 text-amber-300" />
          </span>
        </div>

        {step === "chiedi" && (
          <div data-testid="figura-step-chiedi">
            <p className="font-display text-lg text-amber-100 text-center leading-snug">
              Le carte mostrano una figura{genereTxt} con queste caratteristiche.
            </p>
            <p className="text-[12px] text-purple-200/70 text-center mt-3 leading-relaxed" data-testid="figura-tratti">
              {figura.tratti}
            </p>
            {suggerita && !noSuggerita && (
              <div className="mt-4 rounded-xl border border-amber-500/25 bg-[#1a0b2b] p-3.5" data-testid="figura-suggerita">
                <p className="text-[12px] text-purple-100/85 text-center leading-relaxed">
                  Potrebbe trattarsi della stessa persona di cui avevamo parlato: <span className="text-amber-200">{suggerita.etichetta}</span>?
                </p>
                <div className="flex justify-center gap-2 mt-3">
                  <button onClick={() => salva(suggerita.etichetta)} disabled={busy} data-testid="figura-suggerita-yes" className="gold-btn px-4 py-1.5 text-[11px] disabled:opacity-50">
                    Sì, è lei/lui
                  </button>
                  <button onClick={() => setNoSuggerita(true)} data-testid="figura-suggerita-no" className="text-[11px] text-purple-300/70 hover:text-amber-300 border border-[#4a2175] rounded-full px-4 py-1.5 transition-colors">
                    No, è un'altra persona
                  </button>
                </div>
              </div>
            )}
            {(!suggerita || noSuggerita) && (
              <div className="mt-4">
                <p className="text-[12px] text-purple-100/80 text-center mb-2">L'hai già individuata?</p>
                <input
                  value={etichetta}
                  onChange={(e) => setEtichetta(e.target.value)}
                  maxLength={120}
                  placeholder="es. è Marco, è la collega, è l'ex…"
                  data-testid="figura-input"
                  className="input-dark text-sm"
                />
                <button
                  onClick={() => salva()}
                  disabled={!etichetta.trim() || busy}
                  data-testid="figura-save-btn"
                  className="gold-btn w-full py-2.5 text-xs mt-3 disabled:opacity-50"
                >
                  {busy ? "Un attimo…" : "Salva nella mia memoria"}
                </button>
              </div>
            )}
            <p className="text-[10px] text-purple-300/45 text-center mt-4">Questa interazione non consuma crediti.</p>
          </div>
        )}

        {step === "pin" && (
          <div data-testid="figura-step-pin">
            <p className="font-display text-lg text-amber-100 text-center leading-snug">Un'ultima cosa</p>
            <p className="text-[12px] text-purple-200/70 text-center mt-3 leading-relaxed">
              Per custodire questa figura nella tua memoria protetta, inserisci il tuo PIN.
            </p>
            <div className="mt-4">
              <PinBoxes onComplete={unlock} testid="figura-pin" resetKey={pinReset} />
            </div>
            <p className="text-[10px] text-purple-300/45 text-center mt-4">Questa interazione non consuma crediti.</p>
          </div>
        )}

        {step === "consenso" && (
          <div data-testid="figura-step-consenso">
            <p className="font-display text-lg text-amber-100 text-center leading-snug">
              Vuoi che tenga presente questa persona anche nelle prossime letture?
            </p>
            <div className="flex justify-center gap-2 mt-5">
              <button onClick={() => consenso(true)} data-testid="figura-consenso-si" className="gold-btn px-5 py-2 text-xs">
                Sì, ricordala
              </button>
              <button onClick={() => consenso(false)} data-testid="figura-consenso-no" className="text-[11px] text-purple-300/70 hover:text-amber-300 border border-[#4a2175] rounded-full px-5 py-2 transition-colors">
                No, solo per questa volta
              </button>
            </div>
            <p className="text-[10px] text-purple-300/45 text-center mt-4 leading-relaxed">
              Non lo chiederò di nuovo per questa persona. Puoi sempre modificare o revocare dal pannello della memoria.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
